import React, { useState, useEffect } from 'react';
import { leadConfigApi } from '../../services/api';
import { invalidateLookup } from '../../services/lookupCache';
import toast from 'react-hot-toast';
import { HiPlus, HiPencil, HiTrash, HiX, HiLink, HiRefresh } from 'react-icons/hi';
import { usePermissions } from '../../hooks/usePermissions';
import PermissionWrapper from '../../components/PermissionWrapper';
import ConfirmDialog from '../../components/ConfirmDialog';

const LeadSources = () => {
  const { canCreate } = usePermissions();
  const [sources, setSources] = useState([]);
  const [publicFormLink, setPublicFormLink] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingSource, setEditingSource] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_active: true
  });
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, onConfirm: null });

  const fetchPublicFormLink = async () => {
    try {
      const response = await leadConfigApi.getPublicFormLink();
      const { token } = response.data.data;
      setPublicFormLink(`${window.location.origin}/public/lead/${token}`);
    } catch {
      setPublicFormLink(null);
    }
  };

  const fetchSources = async () => {
    setLoading(true);
    try {
      const [sourcesRes] = await Promise.all([
        leadConfigApi.getSources(),
        fetchPublicFormLink()
      ]);
      setSources(sourcesRes.data.data);
    } catch (error) {
      toast.error('Failed to fetch lead sources');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSources();
  }, []);

  const handleAdd = () => {
    setEditingSource(null);
    setFormData({
      name: '',
      description: '',
      is_active: true
    });
    setShowForm(true);
  };

  const handleEdit = (source) => {
    setEditingSource(source);
    setFormData({
      name: source.name,
      description: source.description || '',
      is_active: source.is_active === 1 || source.is_active === true
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSource) {
        await leadConfigApi.updateSource(editingSource.id, formData);
        toast.success('Lead source updated successfully');
      } else {
        await leadConfigApi.createSource(formData);
        toast.success('Lead source created successfully');
      }
      invalidateLookup('leads:sources');
      setShowForm(false);
      fetchSources();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save lead source');
    }
  };

  const handleCopyFormLink = async () => {
    if (!publicFormLink) {
      toast.error('Public form link is not available');
      return;
    }
    try {
      await navigator.clipboard.writeText(publicFormLink);
      toast.success('Public form link copied to clipboard');
    } catch {
      toast.error('Failed to copy link');
    }
  };

  const handleRegenerateFormLink = () => {
    setConfirmDialog({
      isOpen: true,
      title: 'Regenerate public form link',
      message:
        'This will invalidate the current shareable link. Anyone using the old link will no longer be able to submit the form.',
      confirmText: 'Regenerate',
      type: 'warning',
      onConfirm: async () => {
        try {
          const response = await leadConfigApi.regeneratePublicFormLink();
          const { token } = response.data.data;
          setPublicFormLink(`${window.location.origin}/public/lead/${token}`);
          toast.success('Public form link regenerated');
        } catch (error) {
          toast.error(error.response?.data?.message || 'Failed to regenerate link');
        }
      }
    });
  };

  const handleDelete = (id) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Lead Source',
      message: 'Are you sure you want to delete this lead source?',
      confirmText: 'Delete',
      type: 'danger',
      onConfirm: async () => {
        try {
          await leadConfigApi.deleteSource(id);
          invalidateLookup('leads:sources');
          toast.success('Lead source deleted successfully');
          fetchSources();
        } catch (error) {
          toast.error(error.response?.data?.message || 'Failed to delete lead source');
        }
      }
    });
  };

  const handleToggleActive = async (source) => {
    try {
      await leadConfigApi.updateSource(source.id, {
        is_active: !(source.is_active === 1 || source.is_active === true)
      });
      invalidateLookup('leads:sources');
      toast.success('Lead source updated successfully');
      fetchSources();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update lead source');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-gray-900">Lead Sources Configuration</h1>
          <p className="text-gray-500 mt-1">Manage lead sources for tracking where leads come from</p>
        </div>
        <PermissionWrapper module="configuration" action="create">
          <button
            onClick={handleAdd}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            <HiPlus className="w-5 h-5" />
            Add Lead Source
          </button>
        </PermissionWrapper>
      </div>

      <div className="card border border-primary-100 bg-primary-50/40">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-gray-900">Public inquiry form</h2>
            <p className="text-xs text-gray-600 mt-1">
              Share this link with patients. Submissions are saved as leads with source{' '}
              <span className="font-medium">Form Link</span>.
            </p>
            {publicFormLink ? (
              <p className="mt-2 text-xs text-primary-800 break-all font-mono bg-white/80 rounded-lg px-2 py-1.5 border border-primary-100">
                {publicFormLink}
              </p>
            ) : (
              <p className="mt-2 text-xs text-gray-500">Link unavailable</p>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={handleCopyFormLink}
              disabled={!publicFormLink}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-primary-700 bg-white border border-primary-200 rounded-lg hover:bg-primary-50 disabled:opacity-50"
            >
              <HiLink className="w-4 h-4" />
              Copy link
            </button>
            <PermissionWrapper module="configuration" action="update">
              <button
                type="button"
                onClick={handleRegenerateFormLink}
                disabled={!publicFormLink}
                className="p-2 text-gray-500 bg-white border border-gray-200 rounded-lg hover:text-amber-600 hover:border-amber-200 disabled:opacity-50"
                title="Regenerate link"
              >
                <HiRefresh className="w-5 h-5" />
              </button>
            </PermissionWrapper>
          </div>
        </div>
      </div>

      <div className="card">
        {sources.length === 0 ? (
          <div className="text-center p-8 text-gray-500">
            No lead sources found. Click "Add Lead Source" to create one.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left p-4 font-semibold text-gray-700">Name</th>
                  <th className="text-left p-4 font-semibold text-gray-700">Description</th>
                  <th className="text-center p-4 font-semibold text-gray-700">Status</th>
                  <th className="text-right p-4 font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sources.map((source) => (
                  <tr key={source.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="p-4 font-medium text-gray-900">{source.name}</td>
                    <td className="p-4 text-gray-600">{source.description || '-'}</td>
                    <td className="p-4 text-center">
                      <button
                        onClick={() => handleToggleActive(source)}
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          (source.is_active === 1 || source.is_active === true)
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {(source.is_active === 1 || source.is_active === true) ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center justify-end gap-2">
                        <PermissionWrapper module="configuration" action="update">
                          <button
                            onClick={() => handleEdit(source)}
                            className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg"
                            title="Edit"
                          >
                            <HiPencil className="w-5 h-5" />
                          </button>
                        </PermissionWrapper>
                        <PermissionWrapper module="configuration" action="delete">
                          <button
                            onClick={() => handleDelete(source.id)}
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                            title="Delete"
                          >
                            <HiTrash className="w-5 h-5" />
                          </button>
                        </PermissionWrapper>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold">
                {editingSource ? 'Edit Lead Source' : 'Add Lead Source'}
              </h3>
              <button
                onClick={() => setShowForm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <HiX className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="e.g., Google Ads, JustDial"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Brief description of this lead source"
                />
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <span className="text-sm font-medium text-gray-900">Active</span>
                  <p className="text-xs text-gray-500 mt-1">Enable or disable this lead source</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  {editingSource ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ isOpen: false, onConfirm: null })}
        onConfirm={confirmDialog.onConfirm || (() => {})}
        title={confirmDialog.title || 'Delete Lead Source'}
        message={confirmDialog.message || 'Are you sure you want to delete this lead source?'}
        confirmText={confirmDialog.confirmText || 'Delete'}
        cancelText="Cancel"
        type={confirmDialog.type || 'danger'}
      />
    </div>
  );
};

export default LeadSources;
